import {
  effectiveState,
  findQuestion,
  isAnswered,
  normaliseAnswer,
  resolveQuestion,
  validateAnswer,
  type AnswerInput,
  type ContactInput,
  type EngineState,
  type ResolvedQuestion,
  type Screen,
} from '@aera/schema';
import { tidy } from '@aera/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorNote } from '../components/ui.js';
import type { SaveStatus } from '../lib/autosave.js';
import { Footer, Page } from './Chrome.js';
import { contactEmailInvalid } from './questions/ContactQuestion.js';
import { QuestionBody } from './questions/QuestionBody.js';
import { emptyAnswer } from './questions/common.js';

const CHOICE = new Set(['single', 'multi', 'band', 'pick_explain', 'scale', 'rank']);
const NO_SKIP_RECORD = new Set(['contact', 'checkbox']);
/** AnswerInput takes at most a day on one screen; a tab left open over a weekend must not make saves fail. */
const MAX_ELAPSED_MS = 24 * 3600 * 1000;

/** Same picks, ranks and parts: an answer that normalising left as it was needs no fresh save. */
const sameChoice = (a: AnswerInput, b: AnswerInput) =>
  JSON.stringify([a.selected, a.rank ?? [], a.parts ?? {}]) === JSON.stringify([b.selected, b.rank ?? [], b.parts ?? {}]);

export interface QuestionScreenProps {
  screen: Screen;
  state: EngineState;
  contact: ContactInput | null;
  contactConsent: boolean;
  sessionCode: string;
  saveStatus: SaveStatus;
  onAnswer: (questionId: string, input: AnswerInput, opts?: { debounce?: boolean }) => void;
  onContact: (questionId: string, contact: ContactInput, opts?: { debounce?: boolean }) => void;
  onContactConsent: (questionId: string, agree: boolean) => void;
  onNext: () => void;
  onBack?: () => void;
  busy?: boolean;
}

/**
 * One question per screen (the profile screen shares one between three chip rows). Nothing is required
 * except what the bank marks required; Continue on an empty question records a skip rather than blocking.
 */
export function QuestionScreen(props: QuestionScreenProps) {
  const { screen, state, onAnswer, onNext } = props;
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  // Said beside Continue, where a phone can see it, when a pick is refused at the maximum.
  const [notice, setNotice] = useState<string | null>(null);
  // Resolved against the answers as they stand, so a pick a later change took away (an industry variant,
  // a derived card) is neither offered here nor counted.
  const resolved = useMemo<ResolvedQuestion[]>(() => {
    const eff = effectiveState(state);
    return screen.questionIds.map((id) => resolveQuestion(findQuestion(state.questionnaire, id)!, eff));
  }, [screen, state]);
  const single = resolved.length === 1 ? resolved[0]! : null;

  // Time on screen, added to whatever an earlier visit recorded. Each stretch goes to the question answered
  // in it, so a shared screen's time is counted once and not once per question on it.
  const lastMark = useRef(Date.now());
  const spent = useRef<Record<string, number>>({});
  useEffect(() => {
    lastMark.current = Date.now();
    spent.current = Object.fromEntries(screen.questionIds.map((id) => [id, state.answers[id]?.elapsedMs ?? 0]));
    setError(null);
    setNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.key]);
  const elapsed = (id: string) => {
    const now = Date.now();
    // A clock set back mid-screen must not send a negative time either: the API takes 0 to 24 hours.
    const total = Math.min(MAX_ELAPSED_MS, (spent.current[id] ?? 0) + Math.max(0, now - lastMark.current));
    spent.current[id] = total;
    lastMark.current = now;
    return total;
  };

  /** The stored answer as this screen offers it now: answer fields only, picks no longer offered dropped. */
  const valueOf = (rq: ResolvedQuestion): AnswerInput => {
    const a = state.answers[rq.id];
    return a ? normaliseAnswer(rq, a) : emptyAnswer();
  };

  const change = (rq: ResolvedQuestion, next: AnswerInput, opts?: { debounce?: boolean }) => {
    setError(null);
    setNotice(null);
    onAnswer(rq.id, { ...normaliseAnswer(rq, next), elapsedMs: elapsed(rq.id) }, opts);
  };

  const mark = (kind: 'unsure' | 'skipped') => {
    for (const rq of resolved) {
      if (NO_SKIP_RECORD.has(rq.type)) continue;
      const value = valueOf(rq);
      if (kind === 'skipped' && isAnswered(value)) {
        // Skip keeps an answer that still stands, less any pick a later change took away (as Continue does).
        if (!validateAnswer(rq, value) && !sameChoice(value, state.answers[rq.id]!)) onAnswer(rq.id, { ...value, elapsedMs: elapsed(rq.id) });
        continue;
      }
      onAnswer(rq.id, { selected: [], unsure: kind === 'unsure', skipped: kind === 'skipped', elapsedMs: elapsed(rq.id) });
    }
    onNext();
  };

  const proceed = () => {
    for (const rq of resolved) {
      if (rq.type === 'contact') {
        if (contactEmailInvalid(props.contact)) return setError(t('question.contact.emailInvalid'));
        continue;
      }
      if (rq.type === 'checkbox') continue;
      const value = valueOf(rq);
      if (!isAnswered(value)) {
        if (rq.required) return setError(t('question.required'));
        onAnswer(rq.id, { selected: [], unsure: false, skipped: true, elapsedMs: elapsed(rq.id) });
        continue;
      }
      // The engine's messages are for developers; the respondent gets a line in their own language.
      if (validateAnswer(rq, value)) {
        return setError(rq.max && value.selected.length > rq.max ? t('question.pickUpTo', { max: rq.max }) : t('question.invalid'));
      }
      // Picks a later change took away go from the stored answer too.
      if (!sameChoice(value, state.answers[rq.id]!)) onAnswer(rq.id, { ...value, elapsedMs: elapsed(rq.id) });
    }
    onNext();
  };

  // Number keys 1–9 pick an option on screens with up to nine choices; Enter continues.
  const proceedRef = useRef(proceed);
  proceedRef.current = proceed;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey) return;
      // A sheet open over the question (the privacy chip's) has the keys: nothing is picked or moved on behind it.
      if (document.querySelector('[role=dialog][aria-modal=true]')) return;
      // The Enter that commits IME composition (pinyin committing Latin letters) belongs to the input.
      if (e.isComposing || e.keyCode === 229) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName ?? '';
      const typing = tag === 'TEXTAREA' || tag === 'SELECT' || (tag === 'INPUT' && (el as HTMLInputElement).type !== 'checkbox');
      if (e.key === 'Enter') {
        if (tag === 'TEXTAREA' && !(e.metaKey || e.ctrlKey)) return;
        // A clicked option card keeps focus; Enter on a ticked card continues (Space still toggles it).
        // On an unticked card Enter keeps its native click, so a keyboard user who Tabs to a card picks
        // it rather than moving on with nothing picked. Any other button or link keeps its own Enter.
        const optionCard = el?.closest('[role=radio],[role=checkbox]');
        const tickedCard = !!optionCard && optionCard.getAttribute('aria-checked') === 'true';
        if ((tag === 'BUTTON' && !tickedCard) || tag === 'A') return;
        e.preventDefault();
        proceedRef.current();
        return;
      }
      if (typing || e.metaKey || e.ctrlKey) return;
      if (!single || !CHOICE.has(single.type) || !/^[1-9]$/.test(e.key)) return;
      const count = single.type === 'scale' ? 5 : single.options.length + (single.allowOther ? 1 : 0);
      if (count > 9) return;
      const target = document.getElementById(`opt-${single.id}-${e.key}`);
      if (target) {
        e.preventDefault();
        target.click();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [single]);

  const required = resolved.some((rq) => rq.required);
  const allowUnsure = !!single && single.allowUnsure;
  const onlyContact = resolved.every((rq) => NO_SKIP_RECORD.has(rq.type));
  const marked = single ? state.answers[single.id] : undefined;

  return (
    <>
      <Page>
        {resolved.map((rq, i) => (
          <section key={rq.id} className={i > 0 ? 'mt-8' : ''} aria-labelledby={`q-${rq.id}-label`}>
            {single ? (
              <h1 id={`q-${rq.id}-label`} className="text-[24px] font-semibold leading-[1.3] tracking-[-0.01em] sm:text-[28px]">
                {/* The consent sentence is the checkbox's own label; saying it twice in a row reads as a stutter. */}
                {rq.type === 'checkbox' ? t('question.consentHeading') : tidy(rq.prompt)}
              </h1>
            ) : (
              <h2 id={`q-${rq.id}-label`} className="text-[17px] font-medium leading-snug">
                {tidy(rq.prompt)}
              </h2>
            )}
            {rq.help && <p className="mt-2 text-[15px] text-ink-2">{tidy(rq.help)}</p>}
            <div className={single ? 'mt-6' : 'mt-3'}>
              <QuestionBody
                rq={rq}
                lang={state.lang}
                compact={!single}
                value={valueOf(rq)}
                onChange={(next, opts) => change(rq, next, opts)}
                onLimit={() => setNotice(t('question.limitReached', { max: rq.max }))}
                contact={props.contact}
                onContact={(c, opts) => props.onContact(rq.id, c, opts)}
                contactConsent={props.contactConsent}
                onContactConsent={(agree) => props.onContactConsent(rq.id, agree)}
                sessionCode={props.sessionCode}
              />
            </div>
          </section>
        ))}

        {marked && (marked.unsure || marked.skipped) && !isAnswered({ ...marked, unsure: false }) && (
          <p className="mt-4 text-[13px] text-ink-2">
            {marked.unsure ? t('question.markedUnsure', { label: single!.unsureLabel }) : t('question.markedSkipped')}
          </p>
        )}

        {!required && !onlyContact && (
          <div className="mt-6 text-[14px] text-ink-2">
            {allowUnsure && (
              <>
                <button type="button" onClick={() => mark('unsure')} className="underline-offset-4 hover:text-black hover:underline">
                  {single!.unsureLabel}
                </button>
                <span aria-hidden className="mx-2 text-ink-3">
                  /
                </span>
              </>
            )}
            <button type="button" onClick={() => mark('skipped')} className="underline-offset-4 hover:text-black hover:underline">
              {t('question.skip')}
            </button>
          </div>
        )}
      </Page>
      {/* Problems show in the sticky footer beside Continue: below a long list they would be off-screen on a phone. */}
      <Footer onBack={props.onBack} onNext={proceed} saveStatus={props.saveStatus} busy={props.busy}>
        {error ? (
          <ErrorNote>{error}</ErrorNote>
        ) : notice ? (
          <p role="status" className="text-center text-[13px] text-ink-2">
            {notice}
          </p>
        ) : null}
      </Footer>
    </>
  );
}
